import { watch, onMounted } from 'vue';
import { useUserStore } from '@/store/userStore';

/**
 * Composable for dynamically updating the favicon based on organization branding.
 *
 * Sets the favicon to the organization's icon if available,
 * otherwise falls back to the default favicon.
 *
 * Also updates the theme-color meta tag to match the organization's primary color.
 *
 * @example
 * // In App.vue or a layout component
 * import { useDynamicFavicon } from '@/composables/useDynamicFavicon';
 *
 * setup() {
 *   useDynamicFavicon();
 * }
 */
export function useDynamicFavicon() {
  const userStore = useUserStore();

  const updateFavicon = (iconUrl) => {
    // Get or create favicon link elements
    let faviconLink = document.querySelector("link[rel='icon']");
    let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");

    if (!faviconLink) {
      faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      document.head.appendChild(faviconLink);
    }

    // Update favicon
    if (iconUrl) {
      faviconLink.href = iconUrl;
      // Also update apple touch icon if organization has an icon
      if (appleTouchIcon) {
        appleTouchIcon.href = iconUrl;
      }
    } else {
      // Fallback to default favicon
      faviconLink.href = '/favicon.ico';
      if (appleTouchIcon) {
        appleTouchIcon.href = '/apple-touch-icon.png';
      }
    }
  };

  const updateThemeColor = (color) => {
    let themeColorMeta = document.querySelector("meta[name='theme-color']");

    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      document.head.appendChild(themeColorMeta);
    }

    // Use organization primary color or default blue
    themeColorMeta.content = color || '#3b82f6';
  };

  const updateBranding = () => {
    const organization = userStore.currentOrganization;

    if (organization?.brand_settings) {
      const { icon, primary_color } = organization.brand_settings;

      // Update favicon with organization icon
      updateFavicon(icon);

      // Update theme color with organization primary color
      updateThemeColor(primary_color);
    } else {
      // Reset to defaults
      updateFavicon(null);
      updateThemeColor(null);
    }
  };

  // Watch for organization changes
  watch(
    () => userStore.currentOrganization,
    () => {
      updateBranding();
    },
    { deep: true }
  );

  // Initial update on mount
  onMounted(() => {
    updateBranding();
  });

  return {
    updateBranding,
    updateFavicon,
    updateThemeColor,
  };
}
